/**
 * src/components/AlphaCardModal.tsx — Test-fixture-only card seed.
 *
 * Rendered ONLY for fixture customers (e2e+...@... emails) — in the gated
 * welcome flow's payment step (and at /profile/payment) as the "[fixture]
 * Seed test card" shortcut. Real customers go through the full Elements flow
 * — they never see this modal because their email doesn't match
 * isTestFixtureEmail.
 *
 *   1. One button: "Set up alpha card" → POST /StripeTestCard
 *   2. Server fixture-bypass: patches Customer.payment_method_status
 *      to 'active' without provisioning a Stripe customer
 *   3. Modal closes; OnboardingChecklist refetches payment status
 *
 * Removable when the e2e harness moves to a different card-seeding
 * path (direct Operations API patch?). Currently the cheapest way to
 * keep e2e tests working without giving them admin tokens.
 */

import { useEffect, useState } from 'react';
import { CreditCard, X } from './icons';
import { attachAlphaTestCard, type PaymentMethodSummary } from '../api/billingApi';
import { PrimaryButton, SecondaryButton } from './ui';

interface AlphaCardModalProps {
  /** Called with the new card summary after a successful attach. */
  onSuccess: (summary: PaymentMethodSummary) => void;
  onClose: () => void;
}

export function AlphaCardModal({ onSuccess, onClose }: AlphaCardModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Match FeedbackWidget's Escape-to-close behavior.
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape' && !busy) onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const summary = await attachAlphaTestCard();
      onSuccess(summary);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set up the alpha card');
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Set up alpha card"
      data-testid="alpha-card-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35, 24, 15, 0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          background: 'var(--surface, #FFFAF1)',
          borderRadius: 'var(--attic-r-md, 12px)',
          boxShadow: 'var(--attic-sh-3, 0 16px 48px rgba(0,0,0,0.18))',
          padding: '20px 20px 16px',
          marginBottom: 'calc(var(--bottom-tab-height, 0px) + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div
            style={{
              fontFamily: 'var(--attic-font-display, var(--font-display))',
              fontSize: 'var(--fs-lg)',
              fontWeight: 600,
              color: 'var(--attic-ink, #1A140E)',
              letterSpacing: '-0.3px',
            }}
          >
            Set up payment method
          </div>
          <button
            type="button"
            aria-label="Close"
            data-testid="alpha-card-close"
            onClick={onClose}
            disabled={busy}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--attic-ink-3, #7D6E5D)',
              cursor: busy ? 'wait' : 'pointer',
              padding: 4,
              lineHeight: 0,
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Alpha pill */}
        <div
          style={{
            display: 'inline-block',
            fontSize: 'var(--fs-2xs)',
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: '#5F8351',
            background: 'rgba(95, 131, 81, 0.12)',
            padding: '3px 9px',
            borderRadius: 999,
            marginBottom: 10,
          }}
        >
          Alpha
        </div>

        <p style={{
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-text-primary, #1A140E)',
          lineHeight: 1.5,
          margin: '0 0 8px',
        }}>
          We’ll set up a default test card on your account.
        </p>
        <p style={{
          fontSize: 'var(--fs-xs)',
          color: 'var(--color-text-muted, #7D6E5D)',
          lineHeight: 1.5,
          margin: '0 0 14px',
        }}>
          During alpha testing, all customers share a Stripe test card so we can run end-to-end billing flows without any real charges. You’ll be able to add your own card after launch.
        </p>

        {/* Card preview — visual that mirrors what BillingSection will show */}
        <div
          data-testid="alpha-card-preview"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--color-border, #E4DED0)',
            background: '#FFFFFF',
            marginBottom: 14,
          }}
        >
          <CreditCard size={18} color="var(--color-text-primary)" aria-hidden="true" />
          <div style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-mono, monospace)',
          }}>
            VISA •••• 4242
          </div>
          <div style={{
            marginLeft: 'auto',
            fontSize: 'var(--fs-2xs)',
            color: 'var(--color-text-muted)',
          }}>
            Default
          </div>
        </div>

        {error && (
          <div
            data-testid="alpha-card-error"
            role="alert"
            style={{
              padding: '8px 10px',
              borderRadius: 8,
              background: 'var(--attic-berry-soft)',
              color: 'var(--attic-berry)',
              fontSize: 'var(--fs-xs)',
              marginBottom: 10,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <SecondaryButton onClick={onClose} disabled={busy} data-testid="alpha-card-cancel">
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirm}
            disabled={busy}
            data-testid="alpha-card-confirm"
          >
            {busy ? 'Setting up…' : 'Set up alpha card'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
